import { CommentType, STPlaceholderType } from '@tencent/slide-sdk-pb-model';
import merge from 'lodash/merge';
import { ORPHAN_INDEX } from '../constants/shape-constants';
import { type INoteSlideData, type IPlaceholderShapeData, type ISlideDataDelta, DataType } from '../data';
import notesMasterTemplate from '../template/notes-master-template.json';
import themeTemplate from '../template/theme-template.json';
import { cloneDeep } from '../utils/clone-deep';
import { record } from '../utils/recorder';
import { RemoveCommentMutation } from '../mutation/comment/remove-comment-mutation';
import { SetCommentPropertiesMutation } from '../mutation/comment/set-comment-properties-mutation';
import { AddCommentMutation } from '../mutation/comment/add-comment-mutation';
import { PageMutationAPI } from './page-mutation-api';
import type { ICommentGroup } from '../data/comment-data';
import type { SlideDataModel } from '../model/slide-data-model';
import type { MasterDataModel } from '../model/master-data-model';
import type { LayoutDataModel } from '../model/layout-data-model';
import type { IPlaceholderShapeDataModel } from '../model/group-shape-data-model';
import type { IShapeData } from '../data';

export class SlideMutationAPI extends PageMutationAPI<SlideDataModel> {
    /**
     * 版式切换函数入口
     * @param masterIndex 新版的母版索引
     * @param layoutIndex 新的版式索引
     */
    @record()
    public changeLayout(masterDataModel: MasterDataModel, layoutDataModel: LayoutDataModel) {
        this.changeLayoutQuickSetting(masterDataModel, layoutDataModel, { addLayoutPlaceholders: true });
    }

    /**
     * tdocs 专用的快速切换版式入口，不同于 office，为非标准的母版和AI生成的母版提供更合理的切换方法
     * 继承文档链接 https://doc.weixin.qq.com/doc/w3_m_TBmyBpMXolmC?scode=AJEAIQdfAAo7kK0upJAHwAZAa9ACc
     *
     * 1. slide 占位符找寻新的父占位符时，只依赖 type，并使用简单的 type 匹配规则
     * 2. 占位符只能一对一匹配，slide 多余的占位符变为孤立占位符
     * 3. 匹配的占位符后续修改 idx 一致，以保证导出无误
     *
     * @param masterDataModel 目标版式的母版
     * @param layoutDataModel 目标版式
     */
    @record()
    public changeLayoutQuickSetting(
        masterDataModel: MasterDataModel,
        layoutDataModel: LayoutDataModel,
        options?: { addLayoutPlaceholders?: boolean },
    ) {
        if (!masterDataModel || !layoutDataModel) return;
        const curLayout = this.model.getLayout();
        if (curLayout?.id === layoutDataModel.id) {
            return;
        }
        this.transaction(() => {
            // 获取所有的占位符
            const curPlaceholders = this.model.placeholders;
            const targetLayoutPlaceholders = layoutDataModel.store.getVisiblePlaceholders();
            // 初始化 slide 上尚未匹配的占位符
            const unMatchedCurPlaceholders = new Map<string, IPlaceholderShapeDataModel>(
                curPlaceholders.map((i) => [i.id, i]),
            );
            // 初始化 layout 上尚未匹配的占位符
            const unMatchedTargetPlaceholders = new Map<string, IPlaceholderShapeDataModel>(
                targetLayoutPlaceholders.map((i) => [i.id, i]),
            );

            // 占位符匹配采用二段匹配，先精确匹配，再降级匹配
            this.transformPlaceholder(layoutDataModel, unMatchedCurPlaceholders, unMatchedTargetPlaceholders, false);
            this.transformPlaceholder(layoutDataModel, unMatchedCurPlaceholders, unMatchedTargetPlaceholders, true);

            // slide 未匹配的占位符变为无主占位符，没有文本的删掉
            this.addOrRemoveUnMatchedPlaceholders(Array.from(unMatchedCurPlaceholders.values()));
            if (options?.addLayoutPlaceholders) {
                this.addUnMatchedPlaceholders(Array.from(unMatchedTargetPlaceholders.values()), layoutDataModel);
            }

            // 更新 slide 的 layout 指向
            this.updateProps<ISlideDataDelta>({ layoutId: layoutDataModel.id });
        });
    }

    @record()
    public updateSlideVisible(show: boolean) {
        this.updateProps<ISlideDataDelta>({ show: show === false ? false : null });
    }

    /**
     * 是否显示母版上的图形
     * @param showMasterSp
     */
    @record()
    public updateShowMasterSp(showMasterSp: boolean) {
        this.updateProps<ISlideDataDelta>({ showMasterSp });
    }

    @record()
    public addNoteSlide(noteSlide: INoteSlideData) {
        this.transaction(() => {
            const presentationModel = this.model.context.getPresentation();
            let [notesMaster] = [...presentationModel.notesMasterMap.values()];
            let theme = presentationModel.getThemeById(notesMaster?.props?.themeId);
            if (!theme) {
                presentationModel.api.addTheme([
                    {
                        type: DataType.Theme,
                        name: 'notesMasterTheme',
                        id: '',
                        themeElements: cloneDeep(themeTemplate.themeElements),
                    },
                ]);
                theme = presentationModel.themeList[presentationModel.themeList.length - 1];
            }
            if (!notesMaster) {
                presentationModel.api.addNotesMasterSlide({
                    type: DataType.NotesMaster,
                    id: '',
                    themeId: theme.id,
                    notesStyle: notesMasterTemplate.notesStyle,
                    clrMap: notesMasterTemplate.clrMap,
                    cSld: {},
                });
                [notesMaster] = [...presentationModel.notesMasterMap.values()];
            }
            presentationModel.api.addNoteSlide(noteSlide);
        });
    }

    @record()
    public addComments(comments: { groupId: string; commentGroup: ICommentGroup }[]) {
        this.transaction(() => {
            const { commentGroupMap } = this.model.props;
            comments.forEach((comment) => {
                const { groupId, commentGroup } = comment;
                if (commentGroupMap?.[groupId]) return;
                const { commentType, shapeId, anchorPosition, textAnchor } = commentGroup;
                this.request([
                    new AddCommentMutation({
                        pageId: this.model.id,
                        shapeId,
                        groupId,
                        commentType,
                        anchorPosition,
                        textAnchor,
                        createdTimeStamp: new Date().getTime(),
                    }),
                ]);
            });
        });
    }

    @record()
    public removeComments(comments: { groupId: string; commentGroup: ICommentGroup }[]) {
        this.transaction(() => {
            comments.forEach((comment) => {
                const { groupId, commentGroup } = comment;
                if (!this.model.props.commentGroupMap?.[groupId]) return;
                const { commentType, shapeId } = commentGroup;
                this.request([
                    new RemoveCommentMutation({
                        pageId: this.model.id,
                        shapeId,
                        groupId,
                        commentType,
                    }),
                ]);
            });
        });
    }

    /**
     * 删除当前页上的所有批注
     */
    @record()
    public removeAllComments() {
        const { commentGroupMap } = this.model.props;
        if (!commentGroupMap) return;
        this.transaction(() => {
            Object.keys(commentGroupMap).forEach((groupId) => {
                const commentGroup = commentGroupMap[groupId];
                const { commentType, shapeId } = commentGroup;
                this.request([
                    new RemoveCommentMutation({
                        pageId: this.model.id,
                        shapeId,
                        groupId,
                        commentType,
                    }),
                ]);
            });
        });
    }

    @record()
    public setCommentsProperties(comments: { groupId: string; commentGroup: ICommentGroup }[]) {
        const { commentGroupMap } = this.model.store.data;
        this.transaction(() => {
            comments.forEach((comment) => {
                const { groupId, commentGroup } = comment;
                const originalCommentGroup = commentGroupMap?.[groupId];
                if (!originalCommentGroup) return;
                let toUpdateCommentGroup = commentGroup;
                // 仅当批注类型不变时需要合并
                if (commentGroup.commentType === originalCommentGroup.commentType) {
                    toUpdateCommentGroup = merge(cloneDeep(originalCommentGroup), commentGroup);
                } else {
                    // 由文字批注切换为形状批注
                    if (commentGroup.commentType === CommentType.element) {
                        toUpdateCommentGroup = {
                            commentType: CommentType.element,
                            shapeId: toUpdateCommentGroup.shapeId,
                            anchorPosition: toUpdateCommentGroup.anchorPosition,
                        };
                    }
                    // 由形状/文字批注切换为背景批注
                    if (commentGroup.commentType === CommentType.slide) {
                        toUpdateCommentGroup = {
                            commentType: CommentType.slide,
                        };
                    }
                }
                this.request([
                    new SetCommentPropertiesMutation({
                        pageId: this.model.id,
                        groupId,
                        shapeId: toUpdateCommentGroup.shapeId,
                        commentType: toUpdateCommentGroup.commentType,
                        anchorPosition: toUpdateCommentGroup.anchorPosition,
                        textAnchor: toUpdateCommentGroup.textAnchor,
                    }),
                ]);
            });
        });
    }

    /**
     * 在 changeLayout 时，匹配 slide 和 layout 的占位符，并修改 slide 占位符相关属性
     */
    private transformPlaceholder(
        layoutDataModel: LayoutDataModel,
        unMatchedCurPlaceholders: Map<string, IPlaceholderShapeDataModel>,
        unMatchedTargetPlaceholders: Map<string, IPlaceholderShapeDataModel>,
        isDowngrade: boolean,
    ) {
        for (const ph of unMatchedCurPlaceholders.values()) {
            for (const layoutPh of unMatchedTargetPlaceholders.values()) {
                if (
                    this.isPlaceholderMatchedQuickSetting(
                        ph.props.nvPr.ph?.type,
                        layoutPh.props.nvPr.ph?.type,
                        isDowngrade,
                    )
                ) {
                    // 匹配的占位符按照父占位符重新生成，并修改 idx
                    const newPhList = this.transformMatchedPlaceholder(layoutDataModel, layoutPh, ph);
                    newPhList?.[0]?.api.updateProps({ nvPr: { ph: { idx: layoutPh.props.nvPr.ph?.idx } } });
                    unMatchedCurPlaceholders.delete(ph.id);
                    unMatchedTargetPlaceholders.delete(layoutPh.id);
                    break;
                }
            }
        }
    }

    /**
     * 添加或者删除未匹配的占位符
     * @param unMatchedPlaceholders 未匹配的占位符集合
     */
    private addOrRemoveUnMatchedPlaceholders(unMatchedPlaceholders: IPlaceholderShapeDataModel[]) {
        // let quarterPlCount = 0;
        unMatchedPlaceholders.forEach((plShape) => {
            if (plShape.props.isCustomPlaceholder) {
                // if new pl 有文本，添加相关的孤立属性，孤立属性从继承获取
                if (plShape.props.spPr.xfrm) {
                    plShape.api.updateTransform(plShape.props.spPr.xfrm);
                }
                plShape.api.updateProps({ nvPr: { ph: { idx: ORPHAN_INDEX } } });
                // 修正 office 与在线文档不匹配的默认样式
                // 比如没有 anchor 属性，office是居中对齐，在线文档是底部对齐
                // plShape.updateProps({ textBody: {
                //     vert: 'l',
                //     anchor: 'ctr',
                // } });
            } else {
                // if old pl 没有文本， remove placeholder
                this.deleteShapes([plShape]);
            }
        });
    }

    /**
     * 添加未匹配的占位符到当前幻灯片
     * @param unMatchedPlaceholders 未匹配的占位符集合
     * @param targetLayout 目标版式
     */
    private addUnMatchedPlaceholders(
        unMatchedPlaceholders: IPlaceholderShapeDataModel[],
        targetLayout: LayoutDataModel,
    ) {
        const shapesData = unMatchedPlaceholders
            .map((plShape) => this.model.store.getModifyPlaceholderData(plShape, targetLayout))
            .filter(Boolean) as IPlaceholderShapeData[];
        this.addShapes(shapesData);
    }

    /**
     * 转换匹配的占位符
     * @param targetLayout 目标版式
     * @param targetPlaceholders 目标占位符
     * @param oldPlaceholders 当前占位符
     */
    private transformMatchedPlaceholder(
        targetLayout: LayoutDataModel,
        targetPlaceholders: IPlaceholderShapeDataModel,
        oldPlaceholders: IPlaceholderShapeDataModel,
    ) {
        const targetPlShapeData = this.model.store.getModifyPlaceholderData(targetPlaceholders, targetLayout);
        if (!targetPlShapeData) return;
        if ('txBody' in oldPlaceholders.getOwnedData()) {
            merge(targetPlShapeData, {
                txBody: (oldPlaceholders.getOwnedData() as IShapeData).txBody,
            });
        }
        this.deleteShapes([oldPlaceholders]);
        return this.addShapes([targetPlShapeData]);
    }

    /**
     * 美化专用占位符匹配，只使用 type 来匹配
     * @param type1 原始占位符类型
     * @param type2 目标占位符类型
     * @returns {boolean}
     */
    private isPlaceholderMatchedQuickSetting(
        type1: STPlaceholderType | undefined,
        type2: STPlaceholderType | undefined,
        isDowngrade = false,
    ) {
        if (type1 === type2) {
            return true;
        }
        // 不需要降级匹配，直接结束匹配流程
        if (!isDowngrade) {
            return false;
        }
        // 降级匹配，ctrTitle/title 认为是同一个；subTitle/body/undefined 认为是同一个
        if (
            [STPlaceholderType.ctrTitle, STPlaceholderType.title].includes(type1 ?? STPlaceholderType.PBDEFAULT) &&
            [STPlaceholderType.ctrTitle, STPlaceholderType.title].includes(type2 ?? STPlaceholderType.PBDEFAULT)
        ) {
            return true;
        }
        if (
            [STPlaceholderType.subTitle, STPlaceholderType.body, undefined].includes(type1) &&
            [STPlaceholderType.subTitle, STPlaceholderType.body, undefined].includes(type2)
        ) {
            return true;
        }
        return false;
    }
}
